import Vue from "vue";
import Router from "vue-router";
import { getToken } from "@/utils/token";
import store from "@/store";
import { fetchUserPrivilege } from "@/api/modules/privilege";

import Home from "@/views/Home.vue";
import CandidateLogin from "@/views/candidate/CandidateLogin.vue";
import CandidateEntry from "@/views/candidate/CandidateEntry.vue";
import CandidateRoom from "@/views/candidate/CandidateRoom.vue";
import InvitePage from "@/views/Invite.vue";

import InterviewerLogin from "@/views/interviewer/InterviewerLogin.vue";
import InterviewerLayout from "@/views/interviewer/InterviewerLayout.vue";
import InterviewerEntry from "@/views/interviewer/InterviewerEntry.vue";
import InterviewerRoom from "@/views/interviewer/InterviewerRoom.vue";

import InterviewerSchedulePage from "@/views/interviewer/pages/InterviewerSchedulePage.vue";
import InterviewerReviewPage from "@/views/interviewer/pages/InterviewerReviewPage.vue";
import InterviewerManagePage from "@/views/interviewer/pages/InterviewerManagePage.vue";
import InterviewDetailPage from "@/views/interviewer/pages/interviewDetail.vue";
import HrManagePage from "@/views/interviewer/pages/HrManagePage.vue";
import ExceptionBehaviorConfigPage from "@/views/interviewer/pages/ExceptionBehaviorConfigPage.vue";

import InterviewReview from "@/views/review/InterviewReview.vue";

Vue.use(Router);

const router = new Router({
  mode: "hash",
  routes: [
    { path: "/", name: "home", component: Home },
    { path: "/candidate/login", name: "candidate-login", component: CandidateLogin },
    { path: "/candidate/entry", name: "candidate-entry", component: CandidateEntry },
    { path: "/candidate/room", name: "candidate-room", component: CandidateRoom },
    { path: "/invite", name: "invite", component: InvitePage },

    { path: "/interviewer/login", name: "interviewer-login", component: InterviewerLogin },
    {
      path: "/interviewer/dashboard",
      component: InterviewerLayout,
      redirect: { name: "interviewer-dashboard-schedule" },
      children: [
        {
          path: "schedule",
          name: "interviewer-dashboard-schedule",
          component: InterviewerSchedulePage,
          meta: {
            title: "面试安排",
            subTitle:
              "仅展示待通知 / 待开始 / 进行中 / 待评价的面试，面试结束后流转至面试复核",
            showActions: true,
          },
        },
        {
          path: "review",
          name: "interviewer-dashboard-review",
          component: InterviewerReviewPage,
          meta: {
            title: "面试复核",
            subTitle: "集中处理已结束面试，复核结果独立于面试结果",
            showActions: true,
          },
        },
        {
          path: "interview-detail",
          name: "interviewDetail",
          component: InterviewDetailPage,
          meta: {
            title: "候选人详情",
            subTitle: "候选人基础信息与简历预览",
            showActions: false,
          },
        },
        {
          path: "interviewers",
          name: "interviewer-dashboard-interviewers",
          component: InterviewerManagePage,
          meta: {
            title: "面试官管理",
            subTitle: "维护面试官基本信息",
            showActions: false,
          },
        },
        {
          path: "hrs",
          name: "interviewer-dashboard-hrs",
          component: HrManagePage,
          meta: {
            title: "招聘HR管理",
            subTitle: "维护招聘HR基本信息",
            showActions: false,
          },
        },
        {
          path: "exception-behavior",
          name: "interviewer-dashboard-exception-behavior",
          component: ExceptionBehaviorConfigPage,
          meta: {
            title: "异常行为配置",
            subTitle: "异常行为监控项与风险等级阈值",
            showActions: false,
          },
        },
      ],
    },
    { path: "/interviewer/entry", name: "interviewer-entry", component: InterviewerEntry },
    { path: "/interviewer/room", name: "interviewer-room", component: InterviewerRoom },

    { path: "/review", name: "review", component: InterviewReview },
    { path: "*", redirect: "/" },
  ],
});

async function ensurePrivilegeLoaded() {
  const privilege = store.state && store.state.privilege ? store.state.privilege : null;
  const hasMenuPaths = privilege && Array.isArray(privilege.menuPaths) && privilege.menuPaths.length > 0;
  const hasScopedButtons =
    privilege &&
    privilege.menuButtonCodesByPath &&
    Object.keys(privilege.menuButtonCodesByPath).length > 0;

  if (hasMenuPaths && hasScopedButtons) return;

  let userInfo = null;
  try {
    const raw = sessionStorage.getItem("user_info") || "";
    if (raw) {
      userInfo = JSON.parse(raw);
    }
  } catch (e) {
    // ignore
  }

  const userId =
    (userInfo && (userInfo.userid || userInfo.user_id)) || "";
  if (!userId) return;

  try {
    const res = await fetchUserPrivilege(userId);
    const root = res && (res.data || res.response || res.res || res);
    const items = Array.isArray(root && root.data)
      ? root.data
      : Array.isArray(root)
      ? root
      : [];

    const menuPaths = [];
    const buttonCodes = [];
    const menuButtonCodesByPath = {};

    const walk = (nodes = [], currentMenuPath = null) => {
      nodes.forEach((n) => {
        if (n.menu_type === "MENU") {
          const nextMenuPath = n.path || currentMenuPath;
          if (n.path) menuPaths.push(n.path);
          if (n.children && n.children.length) {
            walk(n.children, nextMenuPath);
          }
          return;
        }

        if (n.menu_type === "BUTTON") {
          if (n.code) {
            buttonCodes.push(n.code);
            if (currentMenuPath) {
              if (!menuButtonCodesByPath[currentMenuPath]) menuButtonCodesByPath[currentMenuPath] = [];
              menuButtonCodesByPath[currentMenuPath].push(n.code);
            }
          }
          if (n.children && n.children.length) {
            walk(n.children, currentMenuPath);
          }
          return;
        }

        if (n.children && n.children.length) {
          walk(n.children, currentMenuPath);
        }
      });
    };

    walk(items);

    const uniq = (arr) => Array.from(new Set(arr || []));
    const dedupedMenuPaths = uniq(menuPaths);
    const dedupedButtonCodes = uniq(buttonCodes);
    const dedupedMenuButtonCodesByPath = {};
    Object.keys(menuButtonCodesByPath).forEach((k) => {
      dedupedMenuButtonCodesByPath[k] = uniq(menuButtonCodesByPath[k]);
    });

    store.commit("setPrivilege", {
      menuPaths: dedupedMenuPaths,
      buttonCodes: dedupedButtonCodes,
      menuButtonCodesByPath: dedupedMenuButtonCodesByPath,
    });

    try {
      sessionStorage.setItem("privilege_menu_paths", JSON.stringify(dedupedMenuPaths));
      sessionStorage.setItem("privilege_button_codes", JSON.stringify(dedupedButtonCodes));
      sessionStorage.setItem(
        "privilege_menu_button_codes_by_path",
        JSON.stringify(dedupedMenuButtonCodesByPath)
      );
    } catch (e) {
      // ignore
    }
  } catch (e) {
    // ignore
  }
}

router.beforeEach(async (to, from, next) => {
  const publicPaths = new Set(["/", "/candidate/login", "/interviewer/login", "/invite"]);
  const isPublic = publicPaths.has(to.path);
  const token = getToken();

  if (!isPublic && !token) {
    next({ path: "/" });
    return;
  }
  if (to.path.indexOf("/interviewer/dashboard") === 0) {
    await ensurePrivilegeLoaded();
  }
  next();
});

export default router;

